import MessageIcon from '@mui/icons-material/Message'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import SearchIcon from '@mui/icons-material/Search'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import DescriptionIcon from '@mui/icons-material/Description'
import { Box, Grid, InputAdornment, Link, TextField as MuiTextField, Typography } from '@mui/material'
import { FC, useMemo, useState } from 'react'
import { isZakenOrganization, useOrganizationSettings } from '../../utils/useOrganizationSettings'
import {
  Datagrid,
  DateField,
  FunctionField,
  ListContextProvider,
  Pagination,
  TextField,
  useGetList,
  useList,
  useRecordContext,
  useTranslate,
} from 'react-admin'
import AddIconButton from '../../components/buttons/AddIconButton'
import RequestStatusChip from '../../components/cards/ActieverzoekStatusChip'
import CreateDialog from '../../components/CreateDialog'
import { Actieverzoek, Samenwerking } from '../../types/apiRequests'
import CreateRequestForm from './Forms/CreateRequestForm'
import EditRequestDialog from '../../components/EditRequestDialog'
import RequestReactionsDialog from '../../components/RequestReactionsDialog'
import RequestDocumentsDialog from '../../components/RequestDocumentsDialog'
import { getCaseUrl, KENMERK_SYSTEEM } from '../../utils/getCaseUrl'

const CollaborationsRequests: FC = () => {
  const translate = useTranslate()
  const record = useRecordContext<Samenwerking>()
  const isClosed = record?.status === 'GESLOTEN'
  const [search, setSearch] = useState('')
  const orgSettings = useOrganizationSettings()
  const isZaken = isZakenOrganization(orgSettings)

  const { data, isLoading, error } = useGetList<Actieverzoek>(
    `actieverzoeken/${record?.id}`,
    {
      filter: {},
      pagination: { page: 1, perPage: 1000 },
    }
  )

  const filtered = useMemo(() => {
    if (!data) return []
    if (!search.trim()) return data
    const q = search.toLowerCase()
    return data.filter(entry =>
      entry.titel?.toLowerCase().includes(q)
      || entry.zenderNaam?.toLowerCase().includes(q)
      || entry.ontvangerNaam?.toLowerCase().includes(q)
      || entry.status?.toLowerCase().includes(q)
    )
  }, [data, search])

  const listContext = useList<Actieverzoek>({ data: filtered, isLoading, perPage: 10 })

  if (isLoading) return <div />
  if (error) {
    return (
      <Typography color="error">
        {translate('ra.notification.http_error')}
      </Typography>
    )
  }

  return (
    <Grid container spacing={3}>
      <Grid item md={12}>
        <Box
          sx={{
            border: 1,
            borderColor: 'grey.300',
            borderRadius: 1,
            p: 2,
            height: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <PlayCircleOutlineIcon sx={{ mr: '10px' }} fontSize="small" />
              {translate('show.requests')}
              {!isClosed && (
                <CreateDialog
                  resource={`samenwerkingen/${record?.id}/actieverzoeken`}
                  defaultValues={{ samenwerkingId: record?.id }}
                  dialogTitle="data.addRequest"
                  dialogSubtitle="data.addRequestSubtitle"
                  dialogIcon={<PlayCircleOutlineIcon />}
                  renderTrigger={open => <AddIconButton onClick={open} />}
                >
                  <CreateRequestForm />
                </CreateDialog>
              )}
            </Typography>
            <MuiTextField
              size="small"
              placeholder={translate('ra.action.search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
          </Box>

          <ListContextProvider value={listContext}>
            <Datagrid rowClick={false} bulkActionButtons={false}>
              <FunctionField
                label=""
                render={(request: Actieverzoek) => (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {!isClosed && (
                      <EditRequestDialog
                        recordId={request?.id}
                        samenwerkingTitel={record?.titel}
                        actieverzoekTitel={request?.titel}
                        currentStatus={request?.status}
                        renderTrigger={(open) => (
                          <ToggleOnIcon fontSize="small" color="primary" onClick={open} sx={{ cursor: 'pointer' }} />
                        )}
                      />
                    )}
                    <RequestReactionsDialog
                      actieverzoekId={request?.id}
                      samenwerkingTitel={record?.titel}
                      actieverzoekTitel={request?.titel}
                      renderTrigger={(open) => (
                        <MessageIcon fontSize="small" color="primary" onClick={open} sx={{ cursor: 'pointer' }} />
                      )}
                    />
                    <RequestDocumentsDialog
                      actieverzoekId={request?.id}
                      samenwerkingId={record?.id}
                      linkedDocumentIds={(request?.documenten ?? []).map(d => d.documentId)}
                      currentStatus={request?.status}
                      samenwerkingTitel={record?.titel}
                      actieverzoekTitel={request?.titel}
                      renderTrigger={(open) => (
                        <DescriptionIcon fontSize="small" color="primary" onClick={open} sx={{ cursor: 'pointer' }} />
                      )}
                    />
                  </Box>
                )}
              />
              <TextField
                label="data.request"
                source="titel"
              />
              <TextField
                label="data.sender"
                source="zenderNaam"
              />
              <TextField
                label="data.receiver"
                source="ontvangerNaam"
              />
              <DateField
                label={translate('data.date')}
                source="creatieDatumTijd"
              />
              <FunctionField
                label={translate('data.status')}
                render={(a: Actieverzoek) => <RequestStatusChip status={a.status} />}
              />
              {isZaken && (
                <FunctionField
                  label={translate('data.caseNumber')}
                  render={(a: Actieverzoek) => {
                    if (!a.zaak_nummer) return null
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const url = getCaseUrl({ system: KENMERK_SYSTEEM.XXLLNC_ZAKEN, number: String(a.zaak_nummer) })
                    return url ? (
                      <Link href={url} target="_blank" rel="noopener">
                        {a.zaak_nummer}
                      </Link>
                    ) : (
                      <>{a.zaak_nummer}</>
                    )
                  }}
                />
              )}
            </Datagrid>
            <Pagination />
          </ListContextProvider>
        </Box>
      </Grid>
    </Grid>
  )
}

export default CollaborationsRequests
